import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Phone,
  KeyRound,
  Trash2,
  ShieldAlert,
  ArrowLeft,
  CheckCircle2,
} from 'lucide-react'

type Step = 'phone' | 'otp' | 'confirm' | 'done'

type ApiErrorField = { path?: string; message?: string }
type ApiResponse<T> = { success: boolean; message: string; data?: T; errors?: ApiErrorField[] | any }

const CLIENT_API_BASE_URL = 'https://hr-app.capturedata.com/api'
const BD_PHONE_REGEX = /^(\+88)?01[3-9]\d{8}$/

function normalizeBdPhoneNumber(phone: string): string {
  const trimmedPhone = phone.trim()
  if (trimmedPhone.startsWith('+88')) return trimmedPhone
  if (trimmedPhone.startsWith('01')) return `+88${trimmedPhone}`
  return trimmedPhone
}

function normalizeApiError(err: unknown): { message: string; fieldErrors: Record<string, string> } {
  const fieldErrors: Record<string, string> = {}

  if (!err || typeof err !== 'object') {
    return { message: 'Something went wrong. Please try again.', fieldErrors }
  }

  const anyErr = err as any
  const message = typeof anyErr?.message === 'string' ? anyErr.message : 'Something went wrong. Please try again.'

  const errors = anyErr?.errors
  if (Array.isArray(errors)) {
    for (const e of errors) {
      const path = typeof e?.path === 'string' ? e.path : ''
      const msg = typeof e?.message === 'string' ? e.message : ''
      if (path && msg) fieldErrors[path] = msg
    }
  }

  return { message, fieldErrors }
}

async function requestJson<T>(path: string, init: RequestInit): Promise<ApiResponse<T>> {
  const res = await fetch(`${CLIENT_API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers || {}),
    },
  })

  const text = await res.text()
  const json = text ? JSON.parse(text) : null

  if (!res.ok) {
    throw json || { message: res.statusText || 'Request failed' }
  }

  return json as ApiResponse<T>
}

export default function DeleteUser() {
  const [step, setStep] = useState<Step>('phone')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otp, setOtp] = useState('')
  const [token, setToken] = useState<string | null>(null)

  const [confirmChecked, setConfirmChecked] = useState(false)

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isPhoneValid = useMemo(() => BD_PHONE_REGEX.test(phoneNumber.trim()), [phoneNumber])
  const canSendOtp = isPhoneValid && !isSendingOtp
  const canVerifyOtp = isPhoneValid && otp.trim().length === 6 && !isVerifyingOtp
  const canDelete = Boolean(token) && confirmChecked && !isDeleting

  const handleSendOtp = async () => {
    setFieldErrors({})
    const normalizedPhone = normalizeBdPhoneNumber(phoneNumber)
    if (!BD_PHONE_REGEX.test(normalizedPhone)) {
      setFieldErrors({ phoneNumber: 'Invalid Bangladeshi phone number (e.g. 01XXXXXXXXX or +8801XXXXXXXXX).' })
      return
    }

    setIsSendingOtp(true)
    try {
      const res = await requestJson<null>('/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber: normalizedPhone }),
      })
      toast.success(res?.message || 'OTP sent successfully.')
      setStep('otp')
    } catch (err) {
      const normalized = normalizeApiError(err)
      setFieldErrors(normalized.fieldErrors)
      toast.error(normalized.message)
    } finally {
      setIsSendingOtp(false)
    }
  }

  const handleVerifyOtp = async () => {
    setFieldErrors({})
    const normalizedPhone = normalizeBdPhoneNumber(phoneNumber)
    const normalizedOtp = otp.trim()
    if (!BD_PHONE_REGEX.test(normalizedPhone)) {
      setFieldErrors({ phoneNumber: 'Invalid phone number.' })
      return
    }
    if (normalizedOtp.length !== 6) {
      setFieldErrors({ otp: 'OTP must be 6 digits.' })
      return
    }

    setIsVerifyingOtp(true)
    try {
      const res = await requestJson<{ token: string }>('/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber: normalizedPhone, otp: normalizedOtp }),
      })
      const receivedToken = res?.data?.token
      if (!receivedToken) {
        toast.error('OTP verified, but no token was returned. Please try again.')
        return
      }
      setToken(receivedToken)
      toast.success(res?.message || 'OTP verified successfully.')
      setStep('confirm')
    } catch (err) {
      const normalized = normalizeApiError(err)
      setFieldErrors(normalized.fieldErrors)
      toast.error(normalized.message)
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!token) return
    setIsDeleting(true)
    try {
      const res = await requestJson<null>('/profile/me', {
        method: 'DELETE',
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
      toast.success(res?.message || 'Your account data has been deleted.')
      setStep('done')
    } catch (err) {
      const normalized = normalizeApiError(err)
      toast.error(normalized.message)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-600/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-gradient-to-tr from-purple-400/20 to-pink-600/20 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-to-r from-cyan-400/10 to-blue-600/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white/80 backdrop-blur-xl rounded-[5px] shadow-2xl border border-white/20 p-6 sm:p-8">
          <div className="flex items-center justify-center mb-6 w-full">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 text-center to-gray-700 bg-clip-text text-transparent">
                Delete Account
              </h1>
              <p className="text-sm text-gray-600 mt-1 text-center">
                Login with your phone number, verify OTP, then delete.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            {(step === 'otp' || step === 'confirm') && (
              <button
                type="button"
                onClick={() => {
                  setFieldErrors({})
                  setOtp('')
                  setToken(null)
                  setConfirmChecked(false)
                  setStep('phone')
                }}
                className="flex w-full justify-center items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Change phone number
              </button>
            )}

            {/* Phone */}
            <div className="space-y-2">
              <label className="block text-sm text-left font-semibold text-gray-700">Phone number (BD)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={step !== 'phone' && step !== 'otp'}
                  className="w-full pl-10 pr-3 py-3 bg-gray-50/50 border border-gray-200 rounded-[4px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-medium disabled:opacity-70"
                  placeholder="01XXXXXXXXX"
                />
              </div>
              {fieldErrors.phoneNumber && <p className="text-red-500 text-xs font-medium">{fieldErrors.phoneNumber}</p>}
              {/* <div>
                <div className="border rounded-md px-3 py-2">
                  <label className="block text-sm text-left font-semibold text-gray-700 mb-1">For testing, you can use the following credentials:</label>
                  <p className="text-sm text-gray-600">
                    <strong className="mr-3">Phone:</strong> 01686853607
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong className="mr-3">OTP:</strong> 123456
                  </p>
                </div>
              </div> */}
            </div>

            {step === 'phone' && (
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={!canSendOtp}
                style={{ background: 'red' }}
                className="w-full bg-gradient-to-r text-white py-3 px-5 rounded-[4px] bg-red-700 font-semibold disabled:opacity-70"
              >
                {isSendingOtp ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span className="text-sm">Sending OTP...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-sm">Send OTP</span>
                  </div>
                )}
              </button>
            )}

            {step === 'otp' && (
              <>
                {/* OTP */}
                <div className="space-y-2">
                  <label className="block text-sm text-left font-semibold text-gray-700">OTP</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full pl-10 pr-3 py-3 bg-gray-50/50 border border-gray-200 rounded-[4px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-medium"
                      placeholder="6-digit OTP"
                    />
                  </div>
                  {fieldErrors.otp && <p className="text-red-500 text-xs font-medium">{fieldErrors.otp}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={!canVerifyOtp}
                    className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-2.5 px-5 rounded-[4px] hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold disabled:opacity-70"
                  >
                    {isVerifyingOtp ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="text-sm">Verifying...</span>
                      </div>
                    ) : (
                      <span className="text-sm">Verify OTP</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={!canSendOtp}
                    className="w-full bg-white text-gray-900 py-2.5 px-5 rounded-[4px] border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold disabled:opacity-70"
                  >
                    Resend OTP
                  </button>
                </div>
              </>
            )}

            {step === 'confirm' && (
              <div className="space-y-4">
                <div className="rounded-[4px] border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">This action is permanent</p>
                      <p className="text-sm text-amber-800 mt-1">
                        You’re about to delete data for <span className="font-semibold">{phoneNumber.trim()}</span>.
                      </p>
                    </div>
                  </div>
                </div>

                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    I understand that deleting my account data can’t be undone.
                  </span>
                </label>

                <button
                  type="button"
                  onClick={handleDeleteUser}
                  disabled={!canDelete}
                  className="w-full bg-gradient-to-r from-red-600 to-red-600 text-white py-3 px-5 rounded-[4px] hover:from-red-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 font-semibold disabled:opacity-70"
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span className="text-sm">Deleting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">Delete user</span>
                    </div>
                  )}
                </button>
              </div>
            )}

            {step === 'done' && (
              <div className="rounded-[4px] border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">Deletion request completed</p>
                    <p className="text-sm text-emerald-800 mt-1">
                      Your request has been processed. If you still see any data, please allow some time for systems to update.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="absolute inset-x-0 -bottom-6 h-6 bg-gradient-to-t from-indigo-600/20 to-transparent blur-xl rounded-full" />
      </div>
    </div>
  )
}
